import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Calendar, Edit, Share2, ImageIcon, ArrowLeft, Sparkles, Trophy, Award, Lock, Link2, ChevronDown, EyeOff, Home, LayoutDashboard, Heart } from "lucide-react";
import { LikeButton } from "@/components/LikeButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { useRunSegments } from "@/hooks/useRunSegments";
import { useAuth } from "@/hooks/useAuth";
import { useUserVehicles } from "@/hooks/useUserVehicles";
import { UserAvatar } from "@/components/UserAvatar";
import { ROUTES } from "@/lib/routes";
import { useQuery } from "@tanstack/react-query";
import { useSubscription } from "@/hooks/useSubscription";
import RunHeroMap from "@/components/RunHeroMap";
import RunStatsBar from "@/components/RunStatsBar";
import PhotoLightbox from "@/components/PhotoLightbox";
import Header from "@/components/Header";
import LockedContent from "@/components/LockedContent";
import WaitlistModal from "@/components/WaitlistModal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const ANONYMOUS_VIEWER_TOKEN_KEY = 'anonymous-run-viewer-token';

interface RunData {
  id: string;
  user_id: string;
  equipment_type: string;
  start_time: string | null;
  end_time: string | null;
  distance_miles: number | null;
  duration_minutes: number | null;
  geometry: any;
  source: string;
  gpx_file_url: string | null;
  photo_urls: any;
  processing_status: 'pending' | 'processing' | 'completed' | 'error';
  processing_error: string | null;
  created_at: string;
  title: string | null;
  description: string | null;
  elevation_gain: number | null;
  average_speed: number | null;
  event_id: string | null;
  hero_photo_url: string | null;
  vehicle_id: string | null;
  visibility: 'public' | 'private' | 'unlisted' | 'anonymous';
}

const getAnonymousViewerToken = () => {
  const existingToken = localStorage.getItem(ANONYMOUS_VIEWER_TOKEN_KEY);
  if (existingToken) return existingToken;

  const token = crypto.randomUUID();
  localStorage.setItem(ANONYMOUS_VIEWER_TOKEN_KEY, token);
  return token;
};

const RunDetail = () => {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [runData, setRunData] = useState<RunData | null>(null);
  const [loading, setLoading] = useState(true);
  const { segments: runSegments, isLoading: segmentsLoading } = useRunSegments(runId);
  const [eventData, setEventData] = useState<any>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [hoveredSegmentId, setHoveredSegmentId] = useState<string | null>(null);
  const [waypoints, setWaypoints] = useState<any[]>([]);
  const [segmentGroups, setSegmentGroups] = useState<Array<{
    trailName: string;
    segments: any[];
    totalDistance: number;
    totalDuration: number;
    difficulty: string | null;
    surfaceType: string | null;
  }>>([]);
  const [vehicleData, setVehicleData] = useState<any>(null);
  const [runAchievements, setRunAchievements] = useState<any[]>([]);
  const [mapLocation, setMapLocation] = useState<{
    center: [number, number];
    extent: { xmin: number; ymin: number; xmax: number; ymax: number };
  } | null>(null);
  const [showWaitlistPrompt, setShowWaitlistPrompt] = useState(false);

  const { vehicles } = useUserVehicles(runData?.user_id || '');

  const { data: ownerProfile } = useQuery({
    queryKey: ['run-owner-profile', runData?.user_id],
    queryFn: async () => {
      if (!runData?.user_id) return null;
      const { data, error } = await supabase
        .from('users')
        .select('id, username, avatar_url')
        .eq('id', runData.user_id)
        .maybeSingle();
      if (error) { console.error('Error fetching owner profile:', error); return null; }
      return data;
    },
    enabled: !!runData?.user_id,
    staleTime: 1000 * 60 * 5,
  });

  const { data: ownerFeaturedVehicle } = useQuery({
    queryKey: ['run-owner-featured-vehicle', runData?.user_id],
    queryFn: async () => {
      if (!runData?.user_id) return null;
      const { data, error } = await supabase
        .from('user_vehicles')
        .select('id, primary_photo_url')
        .eq('user_id', runData.user_id)
        .eq('is_featured', true)
        .maybeSingle();
      if (error) return null;
      return data;
    },
    enabled: !!runData?.user_id,
    staleTime: 1000 * 60 * 5,
  });

  const isAnonymousToViewer = runData?.visibility === 'anonymous' && user?.id !== runData?.user_id;
  const ownerAvatarUrl = ownerFeaturedVehicle?.primary_photo_url || ownerProfile?.avatar_url;
  const ownerUsername = ownerProfile?.username;

  useEffect(() => {
    if (runId) {
      fetchRunData();
    }
  }, [runId]);

  useEffect(() => {
    if (!runData?.id || loading || user?.id === runData.user_id) return;

    const recordView = async () => {
      const visitorToken = user ? undefined : getAnonymousViewerToken();
      const { error } = await supabase.rpc('record_run_view', {
        p_run_id: runData.id,
        p_visitor_token: visitorToken,
      });

      if (error) {
        console.error('Error recording run view:', error);
      }
    };

    void recordView();
  }, [loading, runData?.id, runData?.user_id, user]);

  useEffect(() => {
    if (runData?.vehicle_id && vehicles) {
      const vehicle = vehicles.find(v => v.id === runData.vehicle_id);
      setVehicleData(vehicle || null);
    }
  }, [runData?.vehicle_id, vehicles]);

  useEffect(() => {
    if (runSegments.length > 0) {
      const groups: typeof segmentGroups = [];
      let currentGroup: typeof segmentGroups[0] | null = null;

      const sortedSegments = [...runSegments].sort((a, b) => a.segment_order - b.segment_order);

      sortedSegments.forEach(seg => {
        const trailName = seg.trail_segment?.trail_name || 'Unknown Trail';

        if (!currentGroup || currentGroup.trailName !== trailName) {
          currentGroup = {
            trailName,
            segments: [seg],
            totalDistance: seg.distance_miles || 0,
            totalDuration: seg.duration_minutes || 0,
            difficulty: seg.trail_segment?.difficulty || null,
            surfaceType: seg.trail_segment?.surface_type || null,
          };
          groups.push(currentGroup);
        } else {
          currentGroup.segments.push(seg);
          currentGroup.totalDistance += seg.distance_miles || 0;
          currentGroup.totalDuration += seg.duration_minutes || 0;
        }
      });

      setSegmentGroups(groups);
    }
  }, [runSegments]);

  useEffect(() => {
    if (!user && runData && ['public', 'unlisted'].includes(runData.visibility)) {
      const alreadyPrompted = sessionStorage.getItem('waitlist_prompt_shown');
      if (alreadyPrompted) return;

      const timer = setTimeout(() => {
        setShowWaitlistPrompt(true);
        sessionStorage.setItem('waitlist_prompt_shown', 'true');
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [user, runData]);

  const fetchRunData = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_run_with_location', { p_run_id: runId })
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data) {
        const { center_lon, center_lat, bbox_xmin, bbox_ymin, bbox_xmax, bbox_ymax, ...runDataWithoutLocation } = data;

        if (center_lon && center_lat && bbox_xmin && bbox_ymin && bbox_xmax && bbox_ymax) {
          setMapLocation({
            center: [center_lon, center_lat],
            extent: { xmin: bbox_xmin, ymin: bbox_ymin, xmax: bbox_xmax, ymax: bbox_ymax }
          });
        }

        setRunData(runDataWithoutLocation as RunData);
      } else {
        setRunData(null);
      }

      if (data?.event_id) {
        const { data: event } = await supabase
          .from('events')
          .select('title')
          .eq('id', data.event_id)
          .single();
        setEventData(event);
      }

      const { data: waypointsData } = await supabase
        .from('run_waypoints')
        .select('*')
        .eq('run_id', runId)
        .order('created_at', { ascending: true });

      if (waypointsData) {
        setWaypoints(waypointsData);
      }

      const { data: achievementsData } = await supabase
        .from('user_achievements')
        .select(`
          id,
          earned_at,
          achievement:achievements(id, name, description, points)
        `)
        .eq('run_id', runId);

      if (achievementsData) {
        setRunAchievements(achievementsData);
      }
    } catch (error) {
      console.error('Error fetching run data:', error);
      toast({
        title: "Error loading run",
        description: "Failed to load run details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const handleShare = async () => {
    const url = window.location.href;
    const title = runData?.title || 'Check out my run!';
    
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast({
          title: "Link copied!",
          description: "Share this link with friends.",
        });
      }
    } catch (error) {
      // User cancelled share or clipboard failed
      if (error instanceof Error && error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(url);
          toast({
            title: "Link copied!",
            description: "Share this link with friends.",
          });
        } catch {
          toast({
            title: "Unable to share",
            description: "Please copy the URL from your browser's address bar.",
            variant: "destructive",
          });
        }
      }
    }
  };

  const handleEdit = () => {
    navigate(`/log-run?edit=${runId}`);
  };

  const handleSetHeroPhoto = async (photoUrl: string) => {
    try {
      const { error } = await supabase
        .from('runs')
        .update({ hero_photo_url: photoUrl })
        .eq('id', runId);

      if (error) throw error;

      setRunData(prev => prev ? { ...prev, hero_photo_url: photoUrl } : null);
      toast({
        title: "Hero photo updated",
        description: "Your hero photo has been set successfully.",
      });
    } catch (error) {
      console.error('Error setting hero photo:', error);
      toast({
        title: "Error",
        description: "Failed to update hero photo. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePhotoClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const formatEquipmentType = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'jeep': 'Jeep',
      'truck': 'Truck',
      'sxs': 'Side-by-Side (SxS)',
      'atv': 'ATV',
      'dirt_bike': 'Dirt Bike',
      'adv_bike': 'Adventure Bike',
      'other': 'Other'
    };
    return typeMap[type] || type;
  };

  const formatDateTime = (dateTime: string | null) => {
    if (!dateTime) return 'Not specified';
    return new Date(dateTime).toLocaleString();
  };

  const isOwner = user?.id === runData?.user_id;
  const { canAccess } = useSubscription();
  const canViewDetails = isOwner || canAccess('adventurer');
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!runData) {
    const redirectPath = user ? '/dashboard' : '/';
    const buttonText = user ? 'Go to Dashboard' : 'Return to Home';
    const ButtonIcon = user ? LayoutDashboard : Home;

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <EyeOff className="h-16 w-16 mx-auto mb-6 text-muted-foreground" />
            <h1 className="text-2xl font-bold text-foreground mb-4">Run Not Available</h1>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              This run may be private or no longer exists. If you believe this is an error, please contact the run owner.
            </p>
            <Button onClick={() => navigate(redirectPath)}>
              <ButtonIcon className="h-4 w-4 mr-2" />
              {buttonText}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const photosCount = Array.isArray(runData.photo_urls) ? runData.photo_urls.length : 0;
  const trailsCount = segmentGroups.length;
  const achievementsCount = runAchievements.length;
  const totalAchievementPoints = runAchievements.reduce(
    (sum, ua) => sum + (ua.achievement?.points || 0),
    0
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Mobile: Title above map */}
      <div className="md:hidden px-4 py-4 bg-background border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-bold text-foreground">
                {runData.title || 'Untitled Run'}
              </h1>
              {runData.visibility === 'private' && (
                <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                  <Lock className="h-3 w-3" />
                  Private
                </Badge>
              )}
              {runData.visibility === 'unlisted' && (
                <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                  <Link2 className="h-3 w-3" />
                  Link Only
                </Badge>
              )}
            </div>
            {/* Run Owner */}
            {isAnonymousToViewer ? (
              <p className="text-sm text-muted-foreground mt-1">Anonymous</p>
            ) : ownerUsername ? (
              <div
                className="flex items-center gap-2 mt-1 cursor-pointer group"
                onClick={() => navigate(ROUTES.profile(runData.user_id))}
              >
                <UserAvatar
                  username={ownerUsername}
                  avatarUrl={ownerAvatarUrl}
                  size="sm"
                  usePhotoFilter={!!ownerFeaturedVehicle?.primary_photo_url}
                />
                <span className="text-sm text-muted-foreground group-hover:underline">
                  {ownerUsername}
                </span>
              </div>
            ) : null}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">{formatEquipmentType(runData.equipment_type)}</Badge>
              {runData.start_time && (
                <>
                  <span>•</span>
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(runData.start_time).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>
          {isOwner ? (
            <div className="flex gap-2">
              <LikeButton runId={runData.id} size="sm" />
              <Button variant="outline" size="sm" onClick={handleShare}>
                <Share2 className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <LikeButton runId={runData.id} size="sm" />
          )}
        </div>
      </div>

      {/* Hero Map Section with Minimal Overlay */}
      <div className="relative w-full h-[350px] md:h-[400px] overflow-hidden">
        <RunHeroMap
          runId={runData.id}
          heroPhotoUrl={runData.hero_photo_url}
          initialCenter={mapLocation?.center}
          initialExtent={mapLocation?.extent}
          waypoints={waypoints}
          className="w-full h-full"
          isInteractive={isOwner || canAccess('adventurer')}
        />
        
        {/* Desktop: Floating title overlay - hidden on mobile */}
        <div className="absolute top-4 left-4 z-20 hidden md:block">
          <Card className="bg-background/90 backdrop-blur-sm border-border shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-xl font-bold text-foreground">
                  {runData.title || 'Untitled Run'}
                </h1>
                {runData.visibility === 'private' && (
                  <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                    <Lock className="h-3 w-3" />
                    Private
                  </Badge>
                )}
                {runData.visibility === 'unlisted' && (
                  <Badge variant="secondary" className="flex items-center gap-1 text-xs">
                    <Link2 className="h-3 w-3" />
                    Link Only
                  </Badge>
                )}
              </div>
              {/* Run Owner */}
              {isAnonymousToViewer ? (
                <p className="text-sm text-muted-foreground mt-1">Anonymous</p>
              ) : ownerUsername ? (
                <div
                  className="flex items-center gap-2 mt-1 cursor-pointer group"
                  onClick={() => navigate(ROUTES.profile(runData.user_id))}
                >
                  <UserAvatar
                    username={ownerUsername}
                    avatarUrl={ownerAvatarUrl}
                    size="sm"
                    usePhotoFilter={!!ownerFeaturedVehicle?.primary_photo_url}
                  />
                  <span className="text-sm text-muted-foreground group-hover:underline">
                    {ownerUsername}
                  </span>
                </div>
              ) : null}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Badge variant="outline">{formatEquipmentType(runData.equipment_type)}</Badge>
                {runData.start_time && (
                  <>
                    <span>•</span>
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(runData.start_time).toLocaleDateString()}</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Desktop: Action Buttons Overlay - hidden on mobile */}
        <div className="absolute top-4 right-4 z-10 hidden md:flex gap-2">
          <LikeButton runId={runData.id} className="bg-background/90 backdrop-blur-sm" />
          {isOwner && (
            <>
              <Button variant="outline" size="sm" onClick={handleShare} className="bg-background/90 backdrop-blur-sm">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              <Button variant="outline" size="sm" onClick={handleEdit} className="bg-background/90 backdrop-blur-sm">
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Horizontal Stats Bar */}
      <RunStatsBar
        distance={runData.distance_miles}
        duration={runData.duration_minutes}
        averageSpeed={runData.average_speed}
        elevationGain={runData.elevation_gain}
        trailsCount={trailsCount}
        photosCount={photosCount}
        waypointsCount={waypoints.length}
        achievementsCount={0}
        eventName={eventData?.title}
      />

      {/* Content Sections Below Map */}
      <main className="container mx-auto px-4 py-8 relative z-10 bg-background">
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Photo Gallery - Moved up */}
          {photosCount > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Photo Gallery
                  </div>
                  {!canViewDetails && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      Adventurer
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {photosCount} {photosCount === 1 ? 'photo' : 'photos'} from this adventure
                  {isOwner && ' • Click to view or set hero photo'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {canViewDetails ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {runData.photo_urls.map((photo: any, index: number) => {
                      const photoUrl = typeof photo === 'string' ? photo : photo.url;
                      const isHero = photoUrl === runData.hero_photo_url;
                      return (
                        <div 
                          key={index} 
                          className="relative aspect-square rounded-lg overflow-hidden bg-muted group cursor-pointer"
                          onClick={() => handlePhotoClick(index)}
                        >
                          <img
                            src={photoUrl}
                            alt={`Run photo ${index + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          {isHero && (
                            <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                              <ImageIcon className="h-4 w-4" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-sm font-medium">View</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <LockedContent
                    requiredTier="adventurer"
                    featureDescription="view run photos"
                    blurContent={true}
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {runData.photo_urls.slice(0, 4).map((photo: any, index: number) => {
                        const photoUrl = typeof photo === 'string' ? photo : photo.url;
                        return (
                          <div 
                            key={index} 
                            className="relative aspect-square rounded-lg overflow-hidden bg-muted"
                          >
                            <img
                              src={photoUrl}
                              alt={`Run photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </LockedContent>
                )}
              </CardContent>
            </Card>
          )}

          {/* Run Information - with description included */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Run Information
              </CardTitle>
              <CardDescription>
                Details about this adventure
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Run Story / Description */}
              {runData.description && (
                <div className="pb-4 border-b border-border">
                  <label className="text-sm font-medium text-muted-foreground">Run Story</label>
                  <p className="text-foreground whitespace-pre-wrap mt-1">{runData.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Date</label>
                  <p className="text-sm">{formatDateTime(runData.start_time)}</p>
                </div>
                {/* Only show Equipment Type if no vehicle is linked */}
                {!vehicleData && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Equipment</label>
                    <p className="text-sm">{formatEquipmentType(runData.equipment_type)}</p>
                  </div>
                )}
                {vehicleData && (
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Vehicle</label>
                    <div className="flex items-center gap-3 mt-1">
                      {vehicleData.photo_urls && vehicleData.photo_urls[0] && (
                        <img 
                          src={vehicleData.photo_urls[0]} 
                          alt={`${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`}
                          className="w-16 h-16 object-cover rounded border border-border"
                        />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {vehicleData.year} {vehicleData.make} {vehicleData.model}
                          {vehicleData.trim && ` ${vehicleData.trim}`}
                        </p>
                        {vehicleData.modifications && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {vehicleData.modifications}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI-Detected Waypoints */}
          {waypoints.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Waypoints & Points of Interest
                  </div>
                  {!canViewDetails && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <Lock className="h-3 w-3" />
                      Adventurer
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {waypoints.filter(w => w.auto_generated).length > 0 
                    ? `${waypoints.filter(w => w.auto_generated).length} AI-detected waypoint(s) from your photos`
                    : 'Waypoints from your run'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {canViewDetails ? (
                  <div className="space-y-3">
                    {waypoints.map((waypoint) => (
                      <div 
                        key={waypoint.id}
                        className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/20 transition-colors"
                      >
                        <MapPin className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold mb-1">{waypoint.name}</h4>
                          {waypoint.description && (
                            <p className="text-sm text-muted-foreground mb-2">
                              {waypoint.description}
                            </p>
                          )}
                          {waypoint.ai_suggested_title && waypoint.auto_generated && (
                            <p className="text-xs text-muted-foreground italic">
                              Detected: {waypoint.ai_suggested_title}
                            </p>
                          )}
                          {waypoint.photo_urls && waypoint.photo_urls.length > 0 && (
                            <div className="flex gap-2 mt-2">
                              {waypoint.photo_urls.map((url: string, idx: number) => (
                                <img
                                  key={idx}
                                  src={url}
                                  alt={`Waypoint photo ${idx + 1}`}
                                  className="h-16 w-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() => {
                                    setLightboxIndex(runData.photo_urls?.findIndex((p: any) => p === url) || 0);
                                    setLightboxOpen(true);
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <LockedContent
                    requiredTier="adventurer"
                    featureDescription="view waypoint details"
                    blurContent={true}
                  >
                    <div className="space-y-3">
                      {waypoints.slice(0, 2).map((waypoint) => (
                        <div 
                          key={waypoint.id}
                          className="flex items-start gap-3 p-4 border rounded-lg"
                        >
                          <MapPin className="h-5 w-5 mt-0.5 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold mb-1">{waypoint.name}</h4>
                            <p className="text-sm text-muted-foreground">
                              Waypoint details locked
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </LockedContent>
                )}
              </CardContent>
            </Card>
          )}

          {/* Trail Segments Covered */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Trail Segments Explored
              </CardTitle>
              <CardDescription>
                Trails covered during this run
              </CardDescription>
            </CardHeader>
            <CardContent>
              {segmentsLoading ? (
                <div className="text-sm text-muted-foreground">Loading trail segments...</div>
              ) : segmentGroups.length > 0 ? (
                <Accordion type="multiple" className="space-y-2">
                  {segmentGroups.map((group, groupIndex) => (
                    <AccordionItem 
                      key={`${group.trailName}-${groupIndex}`} 
                      value={`group-${groupIndex}`}
                      className="border border-border rounded-lg overflow-hidden"
                    >
                      <AccordionTrigger className="px-4 py-3 hover:bg-accent/50 hover:no-underline">
                        <div className="flex items-center justify-between w-full pr-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground text-left">
                              {group.trailName}
                            </span>
                            {group.segments.length > 1 && (
                              <Badge variant="secondary" className="text-xs">
                                {group.segments.length} segments
                              </Badge>
                            )}
                            {group.difficulty && (
                              <Badge 
                                variant="outline"
                                className={
                                  group.difficulty === 'easy' ? 'border-trail-easy text-trail-easy' :
                                  group.difficulty === 'moderate' ? 'border-trail-moderate text-trail-moderate' :
                                  group.difficulty === 'hard' ? 'border-trail-difficult text-trail-difficult' :
                                  'border-trail-extreme text-trail-extreme'
                                }
                              >
                                {group.difficulty}
                              </Badge>
                            )}
                          </div>
                          <span className="text-sm font-medium text-muted-foreground">
                            {group.totalDistance.toFixed(1)} mi
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-3">
                        <div className="space-y-2 pt-2 border-t border-border">
                          {group.segments.map((segment, segIndex) => (
                            <div 
                              key={segment.id}
                              className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-accent/30 transition-colors"
                              onMouseEnter={() => setHoveredSegmentId(segment.trail_segment_id)}
                              onMouseLeave={() => setHoveredSegmentId(null)}
                            >
                              <div className="flex-1">
                                <p className="text-sm text-foreground">
                                  {segment.trail_segment?.segment_description || `Segment ${segIndex + 1}`}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  {segment.trail_segment?.surface_type && (
                                    <Badge variant="secondary" className="text-xs">
                                      {segment.trail_segment.surface_type}
                                    </Badge>
                                  )}
                                  {segment.pass_number && segment.pass_number > 1 && (
                                    <Badge variant="outline" className="text-xs">
                                      Pass #{segment.pass_number}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <div className="text-sm font-medium text-foreground">
                                  {segment.distance_miles ? `${segment.distance_miles.toFixed(1)} mi` : 'N/A'}
                                </div>
                                {segment.duration_minutes > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    {segment.duration_minutes} min
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-8">
                  No trail segments identified from this run.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Achievements Earned */}
          {runAchievements.length > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Achievements Earned
                </CardTitle>
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                  {totalAchievementPoints} pts
                </Badge>
              </CardHeader>
              <CardContent>
                <TooltipProvider>
                  <div className="overflow-x-auto pb-2 -mx-2 px-2">
                    <div className="flex gap-3 min-w-max">
                      {runAchievements.map((ua) => (
                        <Tooltip key={ua.id}>
                          <TooltipTrigger asChild>
                            <div 
                              className="flex flex-col items-center p-4 rounded-lg border w-32 shrink-0 transition-colors cursor-pointer bg-card hover:bg-accent/50 border-amber-500/30"
                            >
                              <div className="h-12 w-12 rounded-full flex items-center justify-center mb-2 bg-gradient-to-br from-amber-500 to-orange-500">
                                <Award className="h-6 w-6 text-white" />
                              </div>
                              <p className="text-xs font-medium text-center line-clamp-2 text-foreground">
                                {ua.achievement?.name}
                              </p>
                              <Badge variant="secondary" className="mt-2 text-xs">
                                {ua.achievement?.points || 0} pts
                              </Badge>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[200px]">
                            <p className="font-medium">{ua.achievement?.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {ua.achievement?.description}
                            </p>
                            <p className="text-xs text-amber-500 mt-1">
                              Earned on this run!
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                </TooltipProvider>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Photo Lightbox */}
      {lightboxOpen && photosCount > 0 && (
        <PhotoLightbox
          photos={runData.photo_urls.map((p: any) => typeof p === 'string' ? p : p.url)}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onSetHero={isOwner ? handleSetHeroPhoto : undefined}
          heroPhotoUrl={runData.hero_photo_url}
          isOwner={isOwner}
        />
      )}

      {/* Waitlist prompt for signed-out viewers */}
      <WaitlistModal
        open={showWaitlistPrompt}
        onOpenChange={setShowWaitlistPrompt}
      />
    </div>
  );
};

export default RunDetail;